import { createContext, ReactNode, useContext, useEffect, useState } from 'react'
import config from 'lib/config'
import { FeatureEntitlementPlan, fetchFeatureEntitlements } from 'utils/billingApi'

type FeatureEntitlementsState = {
  status: 'loading' | 'ready' | 'error'
  plan: FeatureEntitlementPlan | null
}

const FeatureEntitlementsContext = createContext<FeatureEntitlementsState>({
  status: 'error',
  plan: null,
})

export function FeatureEntitlementsProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<FeatureEntitlementsState>({
    status: config.billingApiBase ? 'loading' : 'error',
    plan: null,
  })

  useEffect(() => {
    let cancelled = false
    if (!config.billingApiBase) {
      setState({ status: 'error', plan: null })
      return
    }
    setState({ status: 'loading', plan: null })
    fetchFeatureEntitlements().then((plan) => {
      if (cancelled) return
      if (!plan) {
        setState({ status: 'error', plan: null })
        return
      }
      setState({ status: 'ready', plan })
    })
    return () => {
      cancelled = true
    }
  }, [])

  return <FeatureEntitlementsContext.Provider value={state}>{children}</FeatureEntitlementsContext.Provider>
}

/** Resolved plan when billing entitlements loaded. Null keeps the env-flag fallback. */
export function useFeaturePlan(): FeatureEntitlementPlan | null {
  const state = useContext(FeatureEntitlementsContext)
  return state.status === 'ready' ? state.plan : null
}
